import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { GitBranch, Github, Database, CheckCircle2, Loader2, ExternalLink, Unplug, KeyRound } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';

export default function VersionControlPage() {
  const [config, setConfig] = useState(null);
  const [pat, setPat] = useState('');
  const [ownerType, setOwnerType] = useState('user');
  const [ownerName, setOwnerName] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [switching, setSwitching] = useState(false);

  const load = async () => {
    try { setConfig(await api.getVcsConfig()); }
    catch (e) { toast.error(e.message); }
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    if (!pat.trim()) return toast.error('Paste your GitHub Personal Access Token');
    if (ownerType === 'org' && !ownerName.trim()) return toast.error('Enter the organization name');
    setConnecting(true);
    try {
      await api.connectGithubVcs({ pat: pat.trim(), owner_type: ownerType, owner_name: ownerName.trim() || undefined });
      toast.success('GitHub connected — repository ready');
      setPat('');
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setConnecting(false); }
  };

  const disconnect = async () => {
    setSwitching(true);
    try { await api.disconnectGithubVcs(); toast.success('GitHub disconnected'); await load(); }
    catch (e) { toast.error(e.message); } finally { setSwitching(false); }
  };

  const switchMode = async (mode) => {
    if (mode === config?.mode) return;
    setSwitching(true);
    try { await api.setVcsMode(mode); toast.success(`Switched to ${mode === 'github' ? 'GitHub' : 'built-in'} version control`); await load(); }
    catch (e) { toast.error(e.message); } finally { setSwitching(false); }
  };

  if (!config) return <div className="p-8 text-muted-foreground" data-testid="vcs-loading">Loading version control…</div>;

  const connected = config.github_connected;
  const mode = config.mode;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8" data-testid="version-control-page">
      <div>
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-primary/10 border border-primary/20"><GitBranch className="w-6 h-6 text-primary" /></div>
          <div>
            <h1 className="text-3xl font-heading font-bold tracking-tight text-foreground">Version Control</h1>
            <p className="text-sm text-muted-foreground mt-1">Choose where your content versions are stored — INZARA's built-in store or your own GitHub repository.</p>
          </div>
        </div>
      </div>

      {/* Mode selector */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button onClick={() => switchMode('builtin')} disabled={switching}
          className={`text-left p-5 rounded-2xl border transition-all ${mode === 'builtin' ? 'border-primary/60 bg-primary/5 ring-1 ring-primary/30' : 'border-border/40 hover:border-border/70'}`}
          data-testid="vcs-mode-builtin">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Database className="w-5 h-5 text-primary" /><span className="font-heading font-semibold">Built-in version control</span></div>
            {mode === 'builtin' && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
          </div>
          <p className="text-sm text-muted-foreground mt-2">Versions are stored inside INZARA. Zero setup, instant history & restore.</p>
        </button>

        <button onClick={() => connected ? switchMode('github') : toast.info('Connect GitHub below first')} disabled={switching}
          className={`text-left p-5 rounded-2xl border transition-all ${mode === 'github' ? 'border-primary/60 bg-primary/5 ring-1 ring-primary/30' : 'border-border/40 hover:border-border/70'} ${!connected ? 'opacity-70' : ''}`}
          data-testid="vcs-mode-github">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Github className="w-5 h-5" /><span className="font-heading font-semibold">GitHub</span></div>
            {mode === 'github' ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : (!connected && <Badge variant="outline" className="text-[10px]">Not connected</Badge>)}
          </div>
          <p className="text-sm text-muted-foreground mt-2">Each saved version becomes a commit in your private repo. Full Git history & restore from any commit.</p>
        </button>
      </div>

      {/* GitHub connection */}
      <Card className="glass-card p-6 rounded-2xl border-border/50" data-testid="github-connection-card">
        {connected ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-500" /><span className="font-heading font-semibold">GitHub connected</span></div>
              <Button variant="outline" size="sm" onClick={disconnect} disabled={switching} className="rounded-lg" data-testid="github-disconnect-btn">
                <Unplug className="w-4 h-4 mr-2" /> Disconnect
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-muted-foreground">Account</p><p className="font-medium" data-testid="github-login">{config.github_login}</p></div>
              <div><p className="text-muted-foreground">Owner type</p><p className="font-medium capitalize">{config.github_owner_type}</p></div>
              <div className="col-span-2"><p className="text-muted-foreground">Repository</p>
                <a href={config.github_repo_url} target="_blank" rel="noreferrer" className="font-medium text-primary inline-flex items-center gap-1 hover:underline" data-testid="github-repo-link">
                  {config.github_owner_name}/{config.github_repo_name} <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2"><Github className="w-5 h-5" /><span className="font-heading font-semibold">Connect GitHub</span></div>
            <p className="text-sm text-muted-foreground">
              Paste a <strong>fine-grained Personal Access Token</strong> with <em>Administration</em> (create repos), <em>Contents</em> (read/write) and <em>Metadata</em> (read) permissions. We'll automatically create a private repo <code className="text-xs bg-muted px-1 rounded">inzara-&lt;tenant&gt;</code>.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Owner type</Label>
                <Select value={ownerType} onValueChange={setOwnerType}>
                  <SelectTrigger data-testid="github-owner-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Personal account</SelectItem>
                    <SelectItem value="org">Organization</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {ownerType === 'org' && (
                <div className="space-y-1.5">
                  <Label>Organization name</Label>
                  <Input value={ownerName} onChange={e => setOwnerName(e.target.value)} placeholder="my-org" data-testid="github-owner-name" />
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Personal Access Token</Label>
              <Input type="password" value={pat} onChange={e => setPat(e.target.value)} placeholder="github_pat_..." data-testid="github-pat-input" />
            </div>
            <Button onClick={connect} disabled={connecting} className="rounded-xl" data-testid="github-connect-btn">
              {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Github className="w-4 h-4 mr-2" />}
              Connect & create repository
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
